/**
 * Contracts list page (M26 PR 3) — /people/contracts.
 *
 * The M12 Contract detail page (/people/contracts/:id) has been live
 * since the Contract entity shipped, but the LIST surface didn't
 * exist — contracts were only reachable from an Employee's master
 * file. This page closes the gap: HR can see every contract in a
 * LegalEntity, filter by status / type / employee, and Excel-export.
 *
 * Per DESIGN_SYSTEM.md §7 the row-click opens the detail in a route
 * (the existing /people/contracts/:id page) rather than a drawer —
 * detail is rich enough to deserve its own page. The empty state
 * documents the brief's locked convention: contracts are created by
 * the Add Employee wizard (M11), not from this surface.
 */

import type { ColumnDef } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, FileText, Plus, RefreshCw, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  DataTable,
  EmptyState,
  EntityDrawer,
  InlineAISuggestion,
  PageHeader,
  WorkflowStateBadge,
} from "../components/ui";
import { api } from "../lib/api";
import type { ContractsListItem } from "../lib/api";
import {
  ALL_ENTITIES,
  useLegalEntityGate,
  useLegalEntityScope,
} from "../lib/legalEntity";

const _STATUS_OPTIONS = [
  { value: "", labelKey: "contract.status_all" },
  { value: "draft", labelKey: "contract.status_draft" },
  { value: "active", labelKey: "contract.status_active" },
  { value: "suspended", labelKey: "contract.status_suspended" },
  { value: "terminated", labelKey: "contract.status_terminated" },
];

const _TYPE_OPTIONS = [
  { value: "", labelKey: "contract.type_all" },
  { value: "unlimited", labelKey: "contract.type_unlimited" },
  { value: "limited", labelKey: "contract.type_limited" },
  { value: "part_time", labelKey: "contract.type_part_time" },
  { value: "remote", labelKey: "contract.type_remote" },
];

function _fmtDate(iso: string | null): string {
  return iso ?? "—";
}

function _daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso + "T00:00:00").getTime() - Date.now();
  return Math.floor(ms / 86_400_000);
}

type NewContractType =
  | "new_hire"
  | "renewal"
  | "amendment"
  | "re_hire"
  | "secondment"
  | "fixed_term_conversion"
  | "indefinite_conversion"
  | "probation_extension"
  | "external_transfer";

interface NewContractDraft {
  type: NewContractType;
  employee_id: string;
  employee_label: string;
  effective_date: string;
  end_date: string;
  position_id: string;
  basic_salary: string;
  note: string;
}

function _emptyDraft(): NewContractDraft {
  return {
    type: "new_hire",
    employee_id: "",
    employee_label: "",
    effective_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    position_id: "",
    basic_salary: "",
    note: "",
  };
}

function NewContractDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<NewContractDraft>(_emptyDraft);
  if (!open) return null;

  const set = (patch: Partial<NewContractDraft>) =>
    setDraft((d) => ({ ...d, ...patch }));

  const handleSubmit = () => {
    // Pragmatic routing per Session 3a.5 §4.1 — POST /api/contracts
    // doesn't exist yet; route to the existing wiring that handles
    // each contract-type creation path. The drawer chrome is the
    // load-bearing UX surface.
    if (draft.type === "new_hire" || draft.type === "re_hire") {
      navigate("/people/employees/new");
      onClose();
      return;
    }
    if (draft.type === "amendment") {
      navigate("/operations/transactions/contract-amendment/new");
      onClose();
      return;
    }
    // Renewal / conversion / probation extension / etc.: route via the
    // contract amendment HR-initiated path with a hint in the URL so
    // that page can pre-fill the reason_code.
    navigate(
      `/operations/transactions/contract-amendment/new?contract_type=${encodeURIComponent(draft.type)}`,
    );
    onClose();
  };

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("contract.new.title", "New Contract")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            {t("contract.new.continue", "Continue →")}
          </button>
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        <div className="rounded-lg border border-[#e9d5ff] bg-[#f5f3ff] px-3 py-2 text-xs text-[#6b21a8]">
          ✦{" "}
          {t(
            "contract.new.intro",
            "Pick a contract type. Tashghil will route you to the correct wizard with the data you've entered here pre-filled.",
          )}
        </div>

        {/* §1 Employee selection */}
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("contract.new.section.employee", "Employee")}
          </label>
          <input
            value={draft.employee_label}
            onChange={(e) =>
              set({ employee_label: e.target.value, employee_id: e.target.value })
            }
            placeholder={t(
              "contract.new.employee_placeholder",
              "Type employee name · code · iqama (or paste UUID)",
            )}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          />
          <p className="mt-1 text-[10px] text-text-tertiary">
            {t(
              "contract.new.employee_hint",
              "Re-hires: leave blank to route through the Add Employee wizard for a fresh record.",
            )}
          </p>
        </div>

        {/* §2 Contract type */}
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("contract.new.section.type", "Contract type")}
          </label>
          <select
            value={draft.type}
            onChange={(e) => set({ type: e.target.value as NewContractType })}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
          >
            <option value="new_hire">{t("contract.new.type.new_hire", "New Hire")}</option>
            <option value="renewal">{t("contract.new.type.renewal", "Renewal")}</option>
            <option value="amendment">{t("contract.new.type.amendment", "Amendment")}</option>
            <option value="re_hire">{t("contract.new.type.re_hire", "Re-Hire")}</option>
            <option value="secondment">{t("contract.new.type.secondment", "Secondment to Another LE")}</option>
            <option value="fixed_term_conversion">{t("contract.new.type.fixed_term_conversion", "Fixed-Term Conversion")}</option>
            <option value="indefinite_conversion">{t("contract.new.type.indefinite_conversion", "Indefinite Conversion")}</option>
            <option value="probation_extension">{t("contract.new.type.probation_extension", "Probation Extension (Article 53)")}</option>
            <option value="external_transfer">{t("contract.new.type.external_transfer", "External Transfer")}</option>
          </select>
        </div>

        {/* §3 Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("contract.new.section.effective", "Effective date")}
            </label>
            <input
              type="date"
              value={draft.effective_date}
              onChange={(e) => set({ effective_date: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("contract.new.section.end", "End date (if fixed-term)")}
            </label>
            <input
              type="date"
              value={draft.end_date}
              onChange={(e) => set({ end_date: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            />
          </div>
        </div>

        {/* §4 Position */}
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("contract.new.section.position", "Position")}
          </label>
          <input
            value={draft.position_id}
            onChange={(e) => set({ position_id: e.target.value })}
            placeholder={t(
              "contract.new.position_placeholder",
              "Position UUID or title (vacant in same LE)",
            )}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
          />
        </div>

        {/* §5 Compensation */}
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("contract.new.section.salary", "Basic salary (SAR)")}
          </label>
          <input
            type="number"
            value={draft.basic_salary}
            onChange={(e) => set({ basic_salary: e.target.value })}
            placeholder="default from Grade band"
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
          />
          <p className="mt-1 text-[10px] text-text-tertiary">
            ✦{" "}
            {t(
              "contract.new.salary_hint",
              "Mutaba'aa Article 60: salary decrease vs prior contract requires signed consent — captured on the next step.",
            )}
          </p>
        </div>

        {/* §6 Note */}
        <div>
          <label className="block text-xs font-medium text-text-secondary">
            {t("contract.new.section.note", "Note (optional)")}
          </label>
          <textarea
            value={draft.note}
            onChange={(e) => set({ note: e.target.value })}
            rows={3}
            className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            placeholder={t(
              "contract.new.note_placeholder",
              "Context the approver should see (e.g., reason for re-hire)",
            )}
          />
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          {t(
            "contract.new.routing_note",
            "After 'Continue →' the wizard for your chosen contract type opens. Probation Extension routes through the Article 53 (90/180-day) Mutaba'aa veto check.",
          )}
        </div>
      </div>
    </EntityDrawer>
  );
}

export function ContractsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  // Respect the top-bar "All entities" scope: when set, fetch
  // contracts across EVERY LE (backend accepts null). The gate above
  // still guarantees a concrete `le` exists (used by row click-through
  // links etc.); the scope only controls the LIST filter.
  const scope = useLegalEntityScope();
  const isAllScope = scope === ALL_ENTITIES;
  const leFilter = isAllScope ? null : le;

  const [status, setStatus] = useState<string>("");
  const [contractType, setContractType] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [showNewDrawer, setShowNewDrawer] = useState(false);

  const q = useQuery({
    queryKey: ["contracts-list", leFilter, status, contractType, search],
    queryFn: () =>
      api.listContracts({
        legalEntityId: leFilter,
        status: status || null,
        contractType: contractType || null,
        employeeSearch: search || null,
      }),
    enabled: !!le,
  });

  const columns = useMemo<ColumnDef<ContractsListItem, unknown>[]>(
    () => [
      {
        id: "contract_number",
        header: t("contract.col_number"),
        accessorFn: (r) => r.contract_number ?? "",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-secondary">
            {row.original.contract_number ?? "—"}
          </span>
        ),
        size: 110,
      },
      {
        id: "employee",
        header: t("contract.col_employee"),
        accessorFn: (r) =>
          `${r.employee.first_name_en} ${r.employee.last_name_en}`,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-text-primary">
              {row.original.employee.first_name_en}{" "}
              {row.original.employee.last_name_en}
            </span>
            <span className="text-xs text-text-tertiary">
              {row.original.employee.employee_code}
            </span>
          </div>
        ),
      },
      {
        id: "position",
        header: t("contract.col_position"),
        accessorFn: (r) => r.position?.title_en ?? "",
        cell: ({ row }) =>
          row.original.position ? (
            <div className="flex flex-col">
              <span className="text-sm text-text-primary">
                {row.original.position.title_en}
              </span>
              <span className="font-mono text-xs text-text-tertiary">
                {row.original.position.position_number_display}
              </span>
            </div>
          ) : (
            <span className="text-xs text-text-tertiary">—</span>
          ),
      },
      {
        id: "contract_type",
        header: t("contract.col_type"),
        accessorKey: "contract_type",
        cell: ({ row }) => (
          <span className="text-sm capitalize text-text-secondary">
            {row.original.contract_type.replace("_", " ")}
          </span>
        ),
        size: 110,
      },
      {
        id: "contract_status",
        header: t("contract.col_status"),
        accessorFn: (r) => r.contract_status ?? "",
        cell: ({ row }) =>
          row.original.contract_status ? (
            <WorkflowStateBadge state={row.original.contract_status} />
          ) : (
            <span className="text-xs text-text-tertiary">—</span>
          ),
        size: 110,
      },
      {
        id: "start_date",
        header: t("contract.col_start"),
        accessorKey: "start_date",
        cell: ({ row }) => _fmtDate(row.original.start_date),
        size: 110,
      },
      {
        id: "end_date",
        header: t("contract.col_end"),
        accessorKey: "end_date",
        cell: ({ row }) => _fmtDate(row.original.end_date),
        size: 110,
      },
      {
        id: "probation_end_date",
        header: t("contract.col_probation_ends"),
        accessorKey: "probation_end_date",
        cell: ({ row }) => {
          const iso = row.original.probation_end_date;
          if (!iso) return <span className="text-xs text-text-tertiary">—</span>;
          const days = _daysUntil(iso);
          const isSoon = days !== null && days >= 0 && days <= 30;
          return (
            <span
              className={
                isSoon
                  ? "rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                  : "text-sm text-text-secondary"
              }
            >
              {iso}
              {isSoon ? ` · ${days}d` : ""}
            </span>
          );
        },
      },
      {
        id: "basic_salary",
        header: t("contract.col_basic_salary"),
        accessorFn: (r) => Number(r.basic_salary_amount),
        cell: ({ row }) => (
          <span className="font-mono text-sm tabular-nums text-text-primary">
            {Number(row.original.basic_salary_amount).toLocaleString(
              undefined,
              { minimumFractionDigits: 2, maximumFractionDigits: 2 },
            )}{" "}
            <span className="text-xs text-text-tertiary">
              {row.original.basic_salary_currency}
            </span>
          </span>
        ),
        size: 140,
      },
    ],
    [t],
  );

  // Tashghil expiry watch — count contracts ending in next 30 days.
  const rows: ContractsListItem[] = q.data ?? [];
  const expiringSoon = useMemo(
    () =>
      rows.filter((r) => {
        if (!r.end_date) return false;
        const d = _daysUntil(r.end_date);
        return d !== null && d >= 0 && d <= 30;
      }),
    [rows],
  );
  const probationSoon = useMemo(
    () =>
      rows.filter((r) => {
        if (!r.probation_end_date) return false;
        const d = _daysUntil(r.probation_end_date);
        return d !== null && d >= 0 && d <= 30;
      }),
    [rows],
  );

  if (gate) return gate;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.contracts")}
        subtitle={t("contract.subtitle")}
        breadcrumbs={[
          { label: t("nav.people"), href: "/people" },
          { label: t("nav.contracts") },
        ]}
      />

      {/* §4.1 — Primary CTA + inline AI suggestions per Session 3a.5 spec. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-text-tertiary">
          {t(
            "contract.list.hint",
            "Click a row to open the contract detail. Use + New Contract for hire / renewal / amendment / re-hire.",
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowNewDrawer(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {t("contract.list.cta.new", "New Contract")}
        </button>
      </div>

      {expiringSoon.length > 0 && (
        <InlineAISuggestion
          agent="tashghil"
          icon={<RefreshCw className="h-4 w-4" />}
          severity="warning"
          headline={t(
            "contract.list.suggestion.expiring_headline",
            `${expiringSoon.length} contract${expiringSoon.length === 1 ? "" : "s"} expire within 30 days.`,
          )}
          body={t(
            "contract.list.suggestion.expiring_body",
            "Tashghil suggests bulk-renewing the routine ones. Open + New Contract and pick 'Renewal' to start the workflow.",
          )}
          primaryAction={{
            label: t("contract.list.suggestion.start_renewal", "Start renewal"),
            onClick: () => setShowNewDrawer(true),
          }}
          suggestionKey="contracts-expiring"
        />
      )}
      {probationSoon.length > 0 && (
        <InlineAISuggestion
          agent="mutabaa"
          icon={<AlertTriangle className="h-4 w-4" />}
          severity="warning"
          headline={t(
            "contract.list.suggestion.probation_headline",
            `${probationSoon.length} probation period${probationSoon.length === 1 ? "" : "s"} end within 30 days.`,
          )}
          body={t(
            "contract.list.suggestion.probation_body",
            "Mutaba'aa Article 53: extending probation beyond 90 days requires signed consent and is capped at 180 days. Decide confirm / extend / terminate before the deadline.",
          )}
          primaryAction={{
            label: t("contract.list.suggestion.open_watchlist", "Probation Watch"),
            onClick: () => navigate("/people/probation-watch"),
          }}
          suggestionKey="contracts-probation"
        />
      )}
      {!q.isLoading && rows.length === 0 && (
        <InlineAISuggestion
          agent="tashghil"
          icon={<Sparkles className="h-4 w-4" />}
          severity="info"
          headline={t(
            "contract.list.suggestion.empty_headline",
            "No contracts found for the current filters.",
          )}
          body={t(
            "contract.list.suggestion.empty_body",
            "Tashghil notes: every active employee should have an active contract. Use + New Contract to create one for a re-hire or to amend an existing one.",
          )}
          primaryAction={{
            label: t("contract.list.suggestion.add_employee", "Add employee"),
            onClick: () => navigate("/people/employees/new"),
          }}
          suggestionKey="contracts-empty"
        />
      )}

      {/* Filter row — kept lightweight; the DataTable's own global
          search handles text matching across visible columns. The
          status / type pickers filter server-side so the page can
          scale without paging the whole table down. */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          <span>{t("contract.filter_status")}</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
          >
            {_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          <span>{t("contract.filter_type")}</span>
          <select
            value={contractType}
            onChange={(e) => setContractType(e.target.value)}
            className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
          >
            {_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          <span>{t("contract.filter_employee_search")}</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("contract.search_placeholder")}
            className="rounded border border-border-subtle bg-surface-default px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={q.isLoading}
        exportFilename="contracts"
        onRowClick={(r) => navigate(`/people/contracts/${r.id}`)}
        emptyState={
          <EmptyState
            icon={FileText}
            headline={t("contract.no_contracts_yet")}
            description={t("contract.no_contracts_description")}
            action={{
              label: t("contract.list.cta.new", "New Contract"),
              onClick: () => setShowNewDrawer(true),
            }}
          />
        }
      />

      <NewContractDrawer
        open={showNewDrawer}
        onClose={() => setShowNewDrawer(false)}
      />
    </div>
  );
}
